<template>
  <div id="publication-stats" class="editor-modal" v-if="!loadingStats">
    <h2>{{$t('publication-stats.title')}}</h2>
    <div v-if="!showEmailForm">
      <span
        class="ellipsis small"
      >{{$t('publication-stats.showing-stats')}} {{selectedDatasetId ? selectedDatasetId.referenceName : visibleStats == "All stats" ? $t('publication-stats.all-stats'):$t('publication-stats.single-stats') }}</span>
      <div class="form-group">
        <select class="form-control" name="section" id="sectionPicker" v-model="visibleStats">
          <option :value="'All stats'">{{$t('publication-stats.all-stats')}}</option>
          <option :value="'Single stats'">{{$t('publication-stats.single-stats')}}</option>
        </select>
      </div>
      <div v-if="visibleStats == 'All stats'" class="stat-vars">
        <p>
          <span class="lighter">{{$t('publication-stats.generated')}}</span>
          <animate-text :number="stats.generated" />
        </p>
        <p>
          <span class="lighter">{{$t('publication-stats.failed')}}</span>
          <animate-text :number="stats.failed" :animateTime="10" />
        </p>
        <p>
          <span class="lighter">{{$t('publication-stats.average')}}</span>
          {{formatAverageDuration}}
        </p>
        <p>
          <span class="lighter">{{$t('publication-stats.activity')}}</span>
          <span v-if="checkDate">{{ new Date(stats.lastUsed ) | dateFormat('DD.MM.YYYY kl. HH:mm')}}</span>
          <span v-else>{{$t('publication-stats.no-activity')}}</span>
        </p>
      </div>

      <div v-if="visibleStats == 'Single stats'" class="single-items">
        <form @submit.stop.prevent class="single-item-form">
          <div class="form-group">
            <div
              style="display: flex; flex-direction: row; align-items:center; border-bottom: 1px solid rgba(143, 151, 156, 0.192);"
            >
              <input
                @input="onFilterChange(singleItem)"
                autocomplete="off"
                style="padding-left: 10px;"
                class="form-control"
                type="text"
                name="singleItem"
                id="singleItem"
                v-model="singleItem"
                :placeholder="$t('publication-stats.search-placeholder')"
              />
              <div>
                <span v-show="searching">
                  <i class="search-icon fa-light fa-circle-notch fa-spin"></i>
                </span>
                <span v-show="!searching">
                  <i class="fa-light fa-search"></i>
                </span>
              </div>
            </div>
          </div>
        </form>
        <div class="result-list">
          <div
            v-for="result in searchResults"
            :key="result.id"
            class="result-item"
            :title="result.referenceName"
            @click="getSingleItemStats(result)"
          >
            <div class="flex-grow-1">
              <div>{{result.referenceName}}</div>
              <div class="text-muted">{{result.id}}</div>
            </div>
            <img src="@/assets/svg/chevronRight.svg" />
          </div>
          <div
            v-if="noMatch"
            class="no-results text-muted"
          >{{$t('tp-data-popover.no-result1')}} '{{singleItem}}' {{$t('tp-data-popover.no-result2')}}</div>
        </div>

        <h6 v-if="selectedDatasetId" style="padding: 0 12px;">{{selectedDatasetId.referenceName}}:</h6>

        <div
          v-if="publicationSingleItemStats && !noDataOnItem"
          class="single-item-statlist stat-vars"
        >
          <p>
            <span class="lighter">{{$t('publication-stats.single-generated')}}</span>
            <animate-text
              :number="publicationSingleItemStats.length"
              :animateTime="20"
              :unit="publicationSingleItemStats.length > 100 ? '+' : ''"
            />
          </p>
          <p>
            <span class="lighter">{{$t('publication-stats.activity')}}</span>
            <span>{{ new Date(publicationSingleItemStats[0].timestamp ) | dateFormat('DD.MM.YYYY kl. HH:mm')}}</span>
          </p>
          <span style="padding: 0 10px;">Status</span>
          <span
            v-if="publicationSingleItemStats[0].error"
            style="padding: 0 10px;"
            class="text-danger"
          >{{publicationSingleItemStats[0].error}}</span>
          <span v-else style="padding: 0 10px;" class="text-success">OK</span>
        </div>
        <div v-else-if="noDataOnItem">
          <span style="padding: 0 10px;">{{$t('publication-stats.no-info')}}</span>
        </div>
      </div>
    </div>
    <div v-else>
      <div v-if="!showOkMessage">
        <span>{{$t('publication-stats.email-form-description')}}</span>
        <form class="email-form" @submit.stop.prevent>
          <radio-button
            :listClasses="'email-radio-btns'"
            lightLabel
            :label="'Timespan'"
            :options="timeSpanOptions"
            v-model="emailForm.timespan"
          />
          <custom-input
            v-model="emailForm.email"
            :name="'emailInput'"
            :inputType="'text'"
            :label="'Email'"
            :placeholder="$t('publication-stats.email-label')"
          />
          <div class="actions">
            <button
              type="submit"
              class="btn btn-xs btn-primary"
              @click="onSendCSVEmail"
              :disabled="!invalidInputEmailForm"
            >
              {{$t('publication-stats.send-email-btn')}}
              <span v-show="sendingLink">
                <div class="fa-light fa-circle-notch fa-spin"></div>
              </span>
            </button>
            <button
              type="button"
              class="btn btn-xs btn-secondary"
              @click="showEmailForm = !showEmailForm"
            >{{$t('back')}}</button>
            <br />
            <span
              v-if="showErrorMessage"
              class="text-danger"
            >{{$t('publication-stats.send-email-error')}}</span>
          </div>
        </form>
      </div>
      <div v-else>
        <p>{{$t('publication-stats.send-email-success')}}</p>
        <button type="button" class="btn btn-xs btn-primary" @click="leaveEmailForm">Ok</button>
      </div>
    </div>
    <div class="footer">
      <button
        class="btn btn-md btn-primary"
        type="reset"
        @click="$emit('close')"
      >{{$t('publication-stats.close')}}</button>
      <div
        tooltip="Get stats as csv"
        tooltip-placement="left"
        class="send-csv"
        @click="showEmailForm = !showEmailForm"
      >
        <i class="fa-light fa-file-csv"></i>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="loading-publication-stats">
      <div class="fa-light fa-circle-notch fa-spin"></div>
    </div>
  </div>
</template>

<script>
import CustomInput from "@/components/CustomInput";
import RadioButton from "@/components/RadioButton";
import AnimateText from "@/components/AnimateText.vue";
import debounce from "lodash/debounce";
export default {
  props: {
    publication: Object,
    template: Object
  },
  data() {
    return {
      showEmailForm: false,
      selectedDatasetId: null,
      stats: null,
      singleItem: "",
      neverUsed: null,
      testDur: 1000,
      visibleStats: "All stats",
      searchResults: [],
      searching: false,
      searched: false,
      publicationSingleItemStats: null,
      noDataOnItem: false,
      noMatch: false,
      onFilterChangeDebounce: debounce(filter => {
        if (filter.length) {
          this.noMatch = false;
          this.search(filter).then(
            () => (this.searching = false),
            () => (this.searching = false)
          );
        } else {
          this.noMatch = true;
          this.searching = false;
        }
      }, 500),
      loadingStats: false,
      timeSpanOptions: [
        {
          value: 1,
          name: "24hours",
          label: this.$t("publication-stats.radio.24h")
        },
        {
          value: 7,
          name: "7days",
          label: this.$t("publication-stats.radio.7d")
        },
        {
          value: 30,
          name: "30days",
          label: this.$t("publication-stats.radio.30d")
        },
        {
          value: -1,
          name: "allTime",
          label: this.$t("publication-stats.radio.all-time")
        }
      ],
      emailForm: {
        timespan: 1,
        email: this.$store.state.user.username
      },
      sendingLink: false,
      showOkMessage: false,
      showErrorMessage: false
    };
  },
  methods: {
    search() {
      this.selectedDatasetId = null;
      this.noDataOnItem = false;
      this.publicationSingleItemStats = null;
      return this.$store
        .dispatch("searchVariables", {
          filter: this.singleItem,
          templateId: this.template.id
        })
        .then(result => {
          this.searchResults = result;
          if (result.length == 0) {
            this.noMatch = true;
          } else {
            this.noMatch = false;
          }
        });
    },
    getSingleItemStats(singleItem) {
      this.searchResults = null;
      this.selectedDatasetId = singleItem ? singleItem : null;
      const info = {
        contentId: singleItem.id,
        publicationId: this.publication.id
      };
      this.publication.id;
      this.$store.dispatch("getSingleItemStats", info).then(result => {
        if (result.length == 0) {
          this.noDataOnItem = true;
        } else {
          this.publicationSingleItemStats = result;
          this.noDataOnItem = false;
        }
      });
    },
    onFilterChange(filter) {
      // console.log(filter)
      this.searching = true;
      this.onFilterChangeDebounce(filter);
    },
    onSendCSVEmail() {
      this.showErrorMessage = false;
      this.sendingLink = true;

      this.$store
        .dispatch("sendStatsLink", {
          publicationId: this.publication.id,
          formData: this.emailForm
        })
        .then(result => {
          this.sendingLink = false;
          this.showOkMessage = true;
        })
        .catch(e => {
          this.showErrorMessage = true;
          this.sendingLink = false;
        });
    },
    leaveEmailForm() {
      this.sendingLink = false;
      this.showOkMessage = false;
      this.showErrorMessage = false;
      this.showEmailForm = false;
    }
  },
  computed: {
    checkDate() {
      if (this.stats.lastUsed === "0001-01-01T00:00:00.000Z") return false;
      else return true;
    },
    formatAverageDuration() {
      if (this.stats.averageDuration > 999) {
        let formatted = Math.floor(this.stats.averageDuration) / 1000;
        return formatted.toFixed(2) + " s";
      } else return Math.floor(this.stats.averageDuration) + " ms";
    },
    invalidInputEmailForm() {
      const emailRegex = /(.+)@(.+){2,}\.(.+){2,}/;
      return (
        emailRegex.test(this.emailForm.email) &&
        this.emailForm.timespan &&
        this.emailForm.email
      );
    }
  },
  components: {
    AnimateText,
    RadioButton,
    CustomInput
  },
  watch: {
    visibleStats: function() {
      if (this.visibleStats == "All stats") {
        (this.selectedDatasetId = null),
          (this.publicationSingleItemStats = null),
          (this.searchResults = null),
          (this.noDataOnItem = false);
      }
    }
  },
  created() {
    this.loadingStats = true;
    this.$store
      .dispatch("getPublicationStats", this.publication.id)
      .then(res => {
        this.stats = res;
        this.loadingStats = false;
      });
  }
};
</script>

<style lang="less">
.loading-publication-stats {
  height: 470px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: scale(1.4);
  opacity: 0.7;
}
#publication-stats {
  padding: 30px 25px 25px 25px;
  h2 {
    margin: 0px ;
  }

  form {
    margin: 10px 0;
    input {
      width: 240px;
      margin-right: 10px;
    }
  }

  .email-form {
    .email-radio-btns {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }
    .actions {
      margin-top: 10px;
      .btn {
        margin-right: 10px;
      }
    }
  }

  .footer {
    margin-top: 40px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    .send-csv {
      cursor: pointer;
      .fa-file-csv {
        transform: scale(1.5);
      }
    }
  }
  .stat-vars {
    display: flex;
    flex-direction: column;
    p {
      padding: 10px 4px;
      // padding: 0.375rem 1.75rem 0.375rem 0.75rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid rgba(143, 151, 156, 0.192);
      span {
        cursor: default;
      }
    }
  }

  input#singleItem {
    border: none;
  }

  .single-item {
    display: flex;
    flex-direction: column;
  }

  .result-list {
    max-height: 300px;
    overflow: auto;
    .result-item {
      display: flex;
      padding: 5px 10px;
      cursor: pointer;
      span {
        flex-grow: 1;
      }
      img {
        display: none;
      }
      &:hover {
        background-color: rgba(219, 218, 218, 0.4);
        > img {
          display: block;
        }
      }

      &.is-selected {
        background-color: rgba(219, 218, 218);
        img {
          display: inline-block;
        }
      }
    }

    .no-results {
      text-align: center;
      padding: 20px;
    }
  }
}
</style>
